// FOC-488 — Interceptores de axios para DPoP en Academy.
// installDpop(api, { loginPath, getToken, onSessionError }) agrega:
//   - Request interceptor: firma el header DPoP para cada request con el
//     método y la URL exacta. En el login no incluye `ath` y no fija
//     Authorization. En el resto firma con `ath = SHA-256(token)` y
//     pone `Authorization: DPoP <token>`.
//   - Response interceptor: cuando una petición distinta del login recibe
//     401 con un código de sesión (TOKEN_INVALID, DPOP_PROOF_INVALID,
//     DEVICE_MISMATCH, APPLICATION_MISMATCH, SESSION_REVOKED) o sin código,
//     dispara onSessionError con el código para que el front muestre el
//     mensaje correspondiente.
//
// FOC-492 — Un request puede marcarse con `skipAuthRefresh: true` en su
// config. En ese caso, el response interceptor NO dispara onSessionError
// aunque llegue 401: el llamador se encarga del error. Lo usan los
// endpoints de verificación (/device/verify, /device/resend) porque un
// 401 ahí significa "venció el token temporal" y la pantalla lo maneja.

import { createProof } from './dpop-client'

// TOKEN_INVALID es el token caducado o emitido por otro entorno. Sin esto
// la app se queda con la sesión muerta y cada pantalla revienta con el 401.
const SESSION_ERRORS = [
  'TOKEN_INVALID',
  'DPOP_PROOF_INVALID',
  'DEVICE_MISMATCH',
  'APPLICATION_MISMATCH',
  'SESSION_REVOKED'
]

export const installDpop = (api, opts) => {
  // En axios 0.x `api.getUri(config)` NO incluye `baseURL`, así que una
  // llamada tipo `api.post("academy/course/getByApp", ...)` devolvía solo
  // `academy/course/getByApp` y el `new URL(..., window.location.origin)`
  // lo resolvía contra el host del navegador (visionbizacademy.com) en vez
  // de contra el baseURL real (api.visionbiz.net/api/). El `htu` firmado
  // en la prueba DPoP quedaba apuntando a otro origen, el gateway lo
  // rechazaba con DPOP.HTU y el front caía al modal con 401 en todo.
  //
  // Construimos la URL manualmente combinando baseURL + url. Si url ya
  // es absoluta, URL() ignora la base y toma la absoluta directo.
  const fullUrl = (config) => {
    const base = (config.baseURL || api.defaults.baseURL || '').replace(/\/+$/, '')
    const path = String(config.url || '').replace(/^\/+/, '')
    const combined = base ? `${base}/${path}` : path

    return new URL(combined, window.location.origin)
  }
  const isLogin = (config) => fullUrl(config).pathname === opts.loginPath

  api.interceptors.request.use(async (config) => {
    const url = fullUrl(config)
    const htu = url.origin + url.pathname
    const method = (config.method || 'get').toUpperCase()
    const token = isLogin(config) ? null : opts.getToken()

    config.headers = config.headers || {}
    config.headers['DPoP'] = await createProof(method, htu, token || undefined)
    if (token) {
      config.headers['Authorization'] = `DPoP ${token}`
    } else if (!isLogin(config)) {
      // Marcamos el request para que el response interceptor sepa que
      // salio sin Authorization (porque getToken() devolvio falsy). Si
      // llega 401, lo tratamos como ruido del cliente y NO disparamos
      // onSessionError.
      config.__dpopSentWithoutAuth = true
    }

    return config
  })

  api.interceptors.response.use(undefined, (error) => {
    const skipAuthRefresh = error.config && error.config.skipAuthRefresh === true
    const sentWithoutAuth = error.config && error.config.__dpopSentWithoutAuth === true
    if (
      error.response &&
      error.response.status === 401 &&
      error.config &&
      !isLogin(error.config) &&
      !skipAuthRefresh &&
      !sentWithoutAuth
    ) {
      // Un 401 sin código es un token que el gateway no acepta: cuenta como
      // TOKEN_INVALID. Los 401 con otro código (p. ej. un OTP incorrecto) son
      // del propio flujo y no cierran la sesión.
      const code = (error.response.data && error.response.data.code) || 'TOKEN_INVALID'
      if (SESSION_ERRORS.includes(code)) opts.onSessionError(code)
    }

    return Promise.reject(error)
  })
}
